import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { TABLE_STORAGE_KEY } from 'ducks/table/table.constants';

import type { ParsedFilterItem } from '@polyai/ui/components/molecules/Filter/Filter.constants';

import { projectInit } from 'actions';
import type { SortQuery } from 'api/resources/conversation/types';

export enum TableType {
  CONVERSATIONS = 'conversations',
  ANALYSES = 'analyses',
  TOPICS = 'topics',
  CACHED_AUDIOS = 'cached_audios',
  METRICS = 'metrics',
  TEST_CASES = 'test_cases',
  TEST_SET_CASES = 'test_set_cases',
  SOURCES = 'sources',
  API_KEYS = 'api_keys',
}

interface TablePayload<T> {
  table: TableType;
  data: T;
}

export interface TableState {
  currentPage: number;
  filters: ParsedFilterItem[];
  sorts: SortQuery[];
  offset: number;
  search: string;
  firstLoadedAt?: string;
}

export type MappedTableState = {
  [key in TableType]: TableState;
};

export const defaultState: TableState = {
  currentPage: 0,
  filters: [],
  sorts: [],
  offset: 0,
  search: '',
  firstLoadedAt: undefined,
};

export const initialState: MappedTableState = {
  [TableType.CONVERSATIONS]: { ...defaultState },
  [TableType.ANALYSES]: { ...defaultState },
  [TableType.TOPICS]: { ...defaultState },
  [TableType.CACHED_AUDIOS]: { ...defaultState },
  [TableType.METRICS]: { ...defaultState },
  [TableType.TEST_CASES]: { ...defaultState },
  [TableType.TEST_SET_CASES]: { ...defaultState },
  [TableType.SOURCES]: { ...defaultState },
  [TableType.API_KEYS]: { ...defaultState },
};

const loadState = (): MappedTableState | undefined => {
  try {
    const serializedState = localStorage.getItem(TABLE_STORAGE_KEY);

    if (serializedState === null) {
      return undefined;
    }

    return {
      ...initialState,
      ...JSON.parse(serializedState),
    } as MappedTableState;
  } catch (_e) {
    return undefined;
  }
};

const tableSlice = createSlice({
  name: 'table',
  initialState: loadState() || initialState,
  reducers: {
    setCurrentPage: (
      state,
      { payload: { table, data: page } }: PayloadAction<TablePayload<number>>,
    ) => {
      state[table].currentPage = page;
    },
    setFilters: (
      state,
      {
        payload: { table, data: filters },
      }: PayloadAction<TablePayload<ParsedFilterItem[]>>,
    ) => {
      state[table].filters = filters;
    },
    setSorts: (
      state,
      {
        payload: { table, data: sorts },
      }: PayloadAction<TablePayload<SortQuery[]>>,
    ) => {
      state[table].sorts = sorts;
    },
    setOffset: (
      state,
      { payload: { table, data: offset } }: PayloadAction<TablePayload<number>>,
    ) => {
      state[table].offset = offset;
    },
    setSearch: (
      state,
      { payload: { table, data: search } }: PayloadAction<TablePayload<string>>,
    ) => {
      state[table].search = search;
    },
    setTable: (
      state,
      {
        payload: { table, data: newState },
      }: PayloadAction<TablePayload<Partial<TableState>>>,
    ) => {
      state[table] = { ...state[table], ...newState };
    },
    setFirstLoadedAt: (
      state,
      {
        payload: { table, data: firstLoadedAt },
      }: PayloadAction<TablePayload<string>>,
    ) => {
      state[table].firstLoadedAt = firstLoadedAt;
    },
    resetTable: (state, { payload: table }: PayloadAction<TableType>) => {
      state[table] = defaultState;
    },
  },
  extraReducers: builder => {
    builder.addCase(projectInit, () => {
      return { ...initialState };
    });
  },
});

export const {
  resetTable,
  setCurrentPage,
  setFilters,
  setFirstLoadedAt,
  setOffset,
  setSearch,
  setSorts,
  setTable,
} = tableSlice.actions;

export default tableSlice.reducer;
